import React, { FC } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';

import { OnboardingModalActions } from '@/components/MainModal/OnboardingModalActions';
import { Button, LoadingButton } from '@/shared/Button';
import StepHeader from '@/shared/StepHeader/StepHeader';

import { StepCommonProps } from '../../../types';

import styles from './TrackingModeStepVariantB.module.scss';

const TrackingModeStepVariantB: FC<StepCommonProps & {
  selectedCardKey: 'visible' | 'silent' | null;
  stepData: Array<{ key: 'visible' | 'silent'; title: string; desc: string }>;
  handleTrackingModeSelection: (cardKey: 'visible' | 'silent') => void;
  handleNextButtonClick: () => void;
  goToPreviousStep: () => void;
  InfoComponent: React.ReactNode;
}> = ({
  selectedCardKey,
  stepData,
  handleTrackingModeSelection,
  handleNextButtonClick,
  goToPreviousStep,
  InfoComponent,
}) => {
  const { t } = useTranslation();

  const effectiveSelectedKey = selectedCardKey ?? 'interactive';

  return (
    <>
      <StepHeader
        title="onboarding:tracking_mode_step.title_variant_b"
        desc=""
      />
      <Grid container spacing={ 2 } className={ styles.container }>
        { stepData.map((data) => {
          const isSelected = effectiveSelectedKey === data.key;

          return (
            <Grid item xs={ 12 } md={ 6 } key={ data.key }>
              <Card
                onClick={ () => handleTrackingModeSelection(data.key) }
                className={ `${ styles.cardGrid } ${ isSelected ? styles.cardSelected : styles.cardUnselected }` }
              >
                { data.key === 'visible' && (
                  <Box
                    className={ styles.recommendedBadge }
                  >
                    { t('onboarding:tracking_mode_step.variant_b.recommended') }
                  </Box>
                ) }

                <CardContent className={ styles.cardContent }>
                  <Box className={ styles.imageContainer }>
                    <img
                      src={
                        data.key === 'visible'
                          ? '/assets/onboarding/trackingtype/interactive.svg'
                          : '/assets/onboarding/trackingtype/automatic.svg'
                      }
                      alt={ `${ data.title } icon` }
                      className={ styles.cardImage }
                    />
                  </Box>

                  <Typography
                    variant="h6"
                    className={ styles.cardTitle }
                  >
                    { t(data.title) }
                  </Typography>

                  { data.key === 'silent' && (
                    <>
                      <Typography
                        variant="body2"
                        className={ styles.cardDescription }
                      >
                        { t('onboarding:tracking_mode_step.variant_b.silent_card.best_for') }
                      </Typography>
                      <Box className={ styles.featureContainer }>
                        <Box className={ styles.featureItem }>
                          <CheckIcon className={ styles.featureIcon } />
                          <Typography
                            variant="body2"
                            className={ styles.featureText }
                          >
                            { t('onboarding:tracking_mode_step.variant_b.silent_card.feature1') }
                          </Typography>
                        </Box>
                        <Box className={ styles.featureItem }>
                          <CheckIcon className={ styles.featureIcon } />
                          <Typography
                            variant="body2"
                            className={ styles.featureText }
                          >
                            { t('onboarding:tracking_mode_step.variant_b.silent_card.feature2') }
                          </Typography>
                        </Box>
                        <Box className={ styles.featureItem }>
                          <CheckIcon className={ styles.featureIcon } />
                          <Typography
                            variant="body2"
                            className={ styles.featureText }
                          >
                            { t('onboarding:tracking_mode_step.variant_b.silent_card.feature3') }
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  ) }

                  { data.key === 'visible' && (
                    <>
                      <Typography
                        variant="body2"
                        className={ styles.cardDescription }
                      >
                        { t('onboarding:tracking_mode_step.variant_b.visible_card.best_for') }
                      </Typography>
                      <Box className={ styles.featureContainer }>
                        <Box className={ styles.featureItem }>
                          <CheckIcon className={ styles.featureIcon } />
                          <Typography
                            variant="body2"
                            className={ styles.featureText }
                          >
                            { t('onboarding:tracking_mode_step.variant_b.visible_card.feature1') }
                          </Typography>
                        </Box>
                        <Box className={ styles.featureItem }>
                          <CheckIcon className={ styles.featureIcon } />
                          <Typography
                            variant="body2"
                            className={ styles.featureText }
                          >
                            { t('onboarding:tracking_mode_step.variant_b.visible_card.feature2') }
                          </Typography>
                        </Box>
                        <Box className={ styles.featureItem }>
                          <CheckIcon className={ styles.featureIcon } />
                          <Typography
                            variant="body2"
                            className={ styles.featureText }
                          >
                            { t('onboarding:tracking_mode_step.variant_b.visible_card.feature3') }
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  ) }
                </CardContent>
              </Card>
            </Grid>
          );
        }) }
      </Grid>

      <Box className={ styles.infoContainer }>
        { InfoComponent }
      </Box>

      <OnboardingModalActions>
        <Box
          className={ styles.buttonsContainer }
        >
          <Button
            variant="outlined"
            size="medium"
            uppercase
            onClick={ goToPreviousStep }
            className={ styles.backButton }
          >
            { t('common:back') }
          </Button>

          <LoadingButton
            variant="contained"
            type="button"
            disableElevation={ false }
            disabled={ !effectiveSelectedKey }
            onClick={ handleNextButtonClick }
            className={ styles.nextButton }
          >
            { t('common:next') }
          </LoadingButton>
        </Box>
      </OnboardingModalActions>
    </>
  );
};

export default TrackingModeStepVariantB;
